import sortBy from "lodash/sortBy";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import Button from "veneer/Button";

import {
  LOAD_DEVICE_EVENTS,
  unloadDeviceEventsAction,
} from "cortex/ui/src/fleet/redux-store/ui-state/device-events/actions";
import { deviceEventsSelector } from "cortex/ui/src/fleet/redux-store/ui-state/device-events/selectors";
import { Device } from "cortex/ui/src/fleet/view/device/model";
import { denormalizeStrategy } from "cortex/ui/src/fleet/view/strategy/utils";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas";
import { deviceStoresLoadedSelector } from "cortex/ui/src/redux-store/devices/selectors";
import {
  defaultObject,
  makeGetAllAsArray,
} from "cortex/ui/src/redux-store/selectors";
import type { RootState } from "cortex/ui/src/redux-store/store";
import { currentDeviceSelector } from "cortex/ui/src/redux-store/ui-state/current-device/selectors";
import { usersSelector } from "cortex/ui/src/redux-store/users/selectors";
import Notification from "cortex/ui/src/ui/notification";
import { formatDateTimeLocal } from "cortex/ui/src/ui/table/formats";
import { Item, Separator, Timeline } from "cortex/ui/src/ui/timeline";
import { User } from "cortex/ui/src/user/model";
import {
  DEFAULT_TIME_FORMAT,
  parseDateWithTimezoneOrOffset,
  utcToLocalFormat,
} from "cortex/ui/src/util/time";
import { Panel } from "cortex/ui/src/view/layout";

import {
  COLOR_CODE_MAP,
  TYPE_ACTION_COLOR_MAP,
  TYPE_ACTION_ICON_MAP,
} from "../constants";
import {
  EventGroup,
  TimelineEvent,
  TimelineFetchParams,
  TimelineFetchResponse,
} from "../model";

const getAllEventsAsArray = makeGetAllAsArray();

type TimelineContentSource = NonNullable<
  ReturnType<typeof denormalizeStrategy>
> & {
  name: string;
};

interface DeviceTimelineProps {
  deviceId: string;
}

function isContentSourceDeploy(event: TimelineEvent): boolean {
  return event.type === "DEPLOY" && (event.title ?? "").includes("system-1-");
}

function getFormattedValue<T>(
  event: Pick<TimelineEvent, "action" | "crash_recovered" | "type">,
  formattedActionMap: Record<string, Record<string, T | [T, T]>>,
  defaultVal: T,
): T {
  const { action, type } = event;
  const processTerminated = event.crash_recovered !== false;

  const actions = formattedActionMap[type];
  if (!actions || !actions[action]) {
    return defaultVal;
  }

  const val = actions[action];
  if (type === "INAPP" && action === "CRASH" && Array.isArray(val)) {
    return processTerminated ? val[0] : val[1];
  }

  return val as T;
}

function getColor(
  event: Pick<TimelineEvent, "action" | "crash_recovered" | "type">,
): string {
  return getFormattedValue<string>(event, TYPE_ACTION_COLOR_MAP, "blue");
}

function getIcon(
  event: Pick<TimelineEvent, "action" | "crash_recovered" | "type">,
): string {
  return getFormattedValue<string>(event, TYPE_ACTION_ICON_MAP, "check-square");
}

function getDescription(event: TimelineEvent): ReactNode {
  const { desc, timestamps = [] } = event;

  if (timestamps.length === 2) {
    return <>{desc} This event happened one more time recently.</>;
  }

  if (timestamps.length > 2) {
    return (
      <>
        {desc} This event happened {timestamps.length - 1} more times recently.
      </>
    );
  }

  return desc;
}

export function convertDateToDeviceTimezone(
  date: string,
  timezone?: string,
): string {
  if (!timezone) {
    return "";
  }

  const deviceTime = parseDateWithTimezoneOrOffset(date, timezone);

  if (typeof deviceTime === "string") {
    return "";
  }

  return deviceTime.format(DEFAULT_TIME_FORMAT);
}

function enrichEvent(
  event: TimelineEvent,
  params: {
    device?: Device | null;
    latestContentSource?: TimelineContentSource | null;
    users?: Record<string, User | undefined>;
  },
): TimelineEvent {
  const { device, latestContentSource, users } = params;

  if (!isContentSourceDeploy(event)) {
    return event;
  }

  const userName = users?.[event.user_id ?? ""]?.name ?? "Unknown User";
  const deviceName = device?.name ?? "Unknown Device";

  let description: ReactNode;

  if (latestContentSource && latestContentSource.name === event.title) {
    const appName = latestContentSource.main?.[0]?.name || "Unknown App";
    const fallbackCreativeCount = latestContentSource.fallback?.length || 0;

    description = (
      <>
        {userName} changed the content source to <b>{appName}</b> with{" "}
        <b>{fallbackCreativeCount}</b> fallback creatives: ID{" "}
        {event.strategy_id}
      </>
    );
  } else {
    description = `${userName} changed the content source: ID ${event.strategy_id}`;
  }

  return {
    ...event,
    desc: description,
    title: deviceName,
  };
}

export function sortEventGroups(
  eventGroups: Record<string, EventGroup>,
): EventGroup[] {
  const sortedGroups = sortBy(
    Object.values(eventGroups),
    (e) => e.date,
  ).reverse();

  sortedGroups.forEach((group) => {
    group.events = sortBy(group.events, (e) => e.event_date).reverse();
  });

  return sortedGroups;
}

function getLatestContentSource(
  state: RootState,
  device: Device | null,
): TimelineContentSource | null {
  if (!device?.strategy_content) {
    return null;
  }

  const { app, global } = state as {
    app: { contentGroups: Parameters<typeof denormalizeStrategy>[2] };
    global: {
      applications: Parameters<typeof denormalizeStrategy>[0];
      userContents: Parameters<typeof denormalizeStrategy>[1];
    };
  };
  const referenceContentSource = denormalizeStrategy(
    global.applications,
    global.userContents,
    app.contentGroups,
    {
      id: device.strategy_id,
      name: device.strategy_content.name,
      strategy: device.strategy_content,
    },
  ) as TimelineContentSource | null;

  return referenceContentSource
    ? {
        ...referenceContentSource,
        name: device.strategy_content.name,
      }
    : null;
}

export function parseEvents(params: {
  device?: Device | null;
  events?: TimelineEvent[];
  latestContentSource?: TimelineContentSource | null;
  users?: Record<string, User | undefined>;
}): {
  eventGroups: EventGroup[];
  firstEventId?: number | string;
  lastEventId?: number | string;
} {
  const { device, events = [], latestContentSource, users } = params;

  if (!events.length) {
    return {
      eventGroups: [],
      firstEventId: undefined,
      lastEventId: undefined,
    };
  }

  let firstEventId: number | string | undefined;
  let lastEventId: number | string | undefined;

  const eventGroupsByDate: Record<string, EventGroup> = {};

  events.forEach((rawEvent) => {
    const event = enrichEvent(rawEvent, {
      device,
      latestContentSource,
      users,
    });

    const { event_date: eventDate, id } = event;

    if (firstEventId === undefined || Number(firstEventId) > Number(id)) {
      firstEventId = id;
    }

    if (lastEventId === undefined || Number(lastEventId) < Number(id)) {
      lastEventId = id;
    }

    const groupDate = utcToLocalFormat(eventDate, "MMMM, YYYY");

    if (!(groupDate in eventGroupsByDate)) {
      eventGroupsByDate[groupDate] = {
        date: eventDate,
        events: [],
        group: groupDate,
      };
    }

    eventGroupsByDate[groupDate].events.push(event);
  });

  return {
    eventGroups: sortEventGroups(eventGroupsByDate),
    firstEventId,
    lastEventId,
  };
}

export default function DeviceTimeline({ deviceId }: DeviceTimelineProps) {
  const dispatch = useAppDispatch();
  const device = useAppSelector(currentDeviceSelector);
  const users = useAppSelector(usersSelector);
  const storesLoaded = useAppSelector(deviceStoresLoadedSelector);

  const events = useAppSelector((state: RootState) => {
    const deviceEvents =
      (deviceEventsSelector(state) as { events?: Record<string, unknown> })
        .events || defaultObject;
    return getAllEventsAsArray({ byId: deviceEvents }) as TimelineEvent[];
  });

  const latestContentSource = useAppSelector((state: RootState) =>
    getLatestContentSource(state, device),
  );

  const [canLoadMore, setCanLoadMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [loadMessage, setLoadMessage] = useState("");

  const parsed = useMemo(
    () =>
      parseEvents({
        device,
        events,
        latestContentSource,
        users,
      }),
    [device, events, latestContentSource, users],
  );

  const { eventGroups, firstEventId, lastEventId } = parsed;

  const fetchTimeline = useCallback(
    (serviceArgs: TimelineFetchParams): Promise<TimelineFetchResponse> => {
      const {
        deviceId: requestedDeviceId,
        firstEventId,
        lastEventId,
      } = serviceArgs;
      const eventIds = [firstEventId, lastEventId].filter(Boolean);

      return createPromiseApiAction(dispatch, LOAD_DEVICE_EVENTS, [
        requestedDeviceId,
        ...eventIds,
      ]) as Promise<TimelineFetchResponse>;
    },
    [dispatch],
  );

  const unloadTimeline = useCallback(() => {
    dispatch(unloadDeviceEventsAction);
  }, [dispatch]);

  const fetchMore = useCallback(() => {
    setIsLoading(true);

    fetchTimeline({
      deviceId,
      firstEventId,
      lastEventId,
    })
      .then((response) => {
        const { data } = response || {};

        if (!data || !data.length) {
          setCanLoadMore(false);
          setLoadMessage("There are no other events available.");
        }
      })
      .catch(() => {
        setLoadMessage("Failed to load events. Please try again.");
      })
      .then(() => {
        setIsLoading(false);
      });
  }, [deviceId, fetchTimeline, firstEventId, lastEventId]);

  useEffect(() => {
    if (storesLoaded) {
      fetchTimeline({ deviceId });
    }
  }, [deviceId, fetchTimeline, storesLoaded]);

  useEffect(() => {
    return () => {
      unloadTimeline();
    };
  }, [unloadTimeline]);

  const renderEventGroup = useCallback((groupEvents: TimelineEvent[]) => {
    return groupEvents.map((event) => (
      <Item
        color={COLOR_CODE_MAP[getColor(event)]}
        content={getDescription(event)}
        deviceTime={convertDateToDeviceTimezone(
          event.event_date,
          device?.timezone,
        )}
        icon={getIcon(event)}
        key={event.id}
        time={formatDateTimeLocal(event.event_date)}
        title={event.title ?? "Unknown Device"}
      />
    ));
  }, []);

  const renderEventGroups = useMemo(() => {
    return eventGroups.flatMap((eventGroup) => {
      const { events: groupedEvents, group: date } = eventGroup;

      return [
        <Separator key={`sep-${date}`} text={date} />,
        ...renderEventGroup(groupedEvents),
      ];
    });
  }, [eventGroups, renderEventGroup]);

  return (
    <Panel title="Device Timeline">
      <Timeline>
        {renderEventGroups}
        <li className="timeline-container">
          <div className="timeline-item">
            <Notification warning={loadMessage} />
            <Button
              disabled={isLoading || !canLoadMore}
              fullWidth
              onClick={fetchMore}
            >
              Load More
            </Button>
          </div>
        </li>
      </Timeline>
    </Panel>
  );
}
